import {
  Avatar,
  Box,
  ButtonBase,
  Checkbox,
  FormControlLabel,
  FormLabel,
  Stack,
  Typography,
} from '@mui/material';
import { ControlledSelectInput2 } from 'Components';
import { ControlledAutocomplete } from 'Components/ControlledAutoComplete';
import { AppliesContext } from 'Context/AppliesContext';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import {
  AccordionArrowPurple,
  CheckedIcon,
  PurpleX,
  UncheckedIcon,
} from 'Images';
import { ignoreHyphenFilterOptions } from 'Utils/ignoreHyphenFilterOptions';
import { replaceZerosWithHyphen } from 'Utils/replaceZerosWithHyphen';
import { useContext } from 'react';

export const FirstSegment = () => {
  const { applications, filtersData, setFiltersData } =
    useContext(AppliesContext);
  const dictionary = useDictionary(Dictionary, DICTIONARY.Applies);
  const isDesktop = useIsDesktop();

  const getAllApplicationTitles = () => [
    ...new Set(
      applications
        .map((application) => application.subject)
        .filter((title) => title && title.trim() !== '')
    ),
  ];
  const getAllRequestNumbers = () => [
    ...new Set(
      applications
        .map((application) =>
          application.shemeshApplyNumber
            ? replaceZerosWithHyphen(application.shemeshApplyNumber)
            : ''
        )
        .filter(
          (shemeshApplyNumber) =>
            shemeshApplyNumber && shemeshApplyNumber.trim() !== ''
        )
    ),
  ];

  const getAllRequestStatuses = () => [
    ...new Set(
      applications
        .map((application) => application.status.description)
        .filter((description) => description && description.trim() !== '')
    ),
  ];

  return (
    <Stack
      gap={isDesktop ? '48px' : '24px'}
      flexDirection={isDesktop ? 'row' : 'column'}
      flexWrap={isDesktop ? 'wrap' : undefined}
    >
      <Box width={isDesktop ? '47%' : '100%'}>
        <ControlledSelectInput2
          disablePortal={isDesktop ? false : true}
          label={dictionary.byRequestTitle}
          options={getAllApplicationTitles()}
          value={filtersData.title}
          onChange={(e) =>
            setFiltersData({ ...filtersData, title: e.target.value })
          }
          size={'small'}
        />
      </Box>
      <Box
        width={isDesktop ? '47%' : '100%'}
        position={'relative'}
        top={'-2px'}
        sx={{
          '.MuiInputLabel-root': {
            display: 'block',
          },
        }}
      >
        <ControlledAutocomplete
          filterOptions={ignoreHyphenFilterOptions}
          freeSolo
          label={dictionary.byRequestNumber}
          options={getAllRequestNumbers()}
          value={filtersData.number}
          setValue={(value: string | null) => {
            setFiltersData({ ...filtersData, number: value ?? '' });
          }}
          inputLabelProps={{
            sx: { display: filtersData.number ? 'none' : 'block' },
          }}
          paperComponent={
            getAllRequestNumbers().filter((value) =>
              value.includes(filtersData.number)
            ).length === 0 && (
              <Stack>
                <Typography variant={'body3'}>
                  {dictionary.noResults}
                </Typography>
              </Stack>
            )
          }
          disableClearIcon
          endAdornment={
            <Stack
              direction={'row'}
              mt={isDesktop ? undefined : '-4px'}
              width={'auto'}
              gap={'15px'}
            >
              {filtersData.number.length > 0 ? (
                <ButtonBase
                  onClick={() => setFiltersData({ ...filtersData, number: '' })}
                  sx={{ mr: '10px' }}
                >
                  <Avatar
                    alt={''}
                    src={PurpleX}
                    sx={{ width: '20px', height: '20px' }}
                  />
                </ButtonBase>
              ) : (
                <Avatar src={AccordionArrowPurple} />
              )}
            </Stack>
          }
        />
      </Box>
      <Box alignSelf={'flex-end'} width={isDesktop ? '47%' : '100%'}>
        <ControlledSelectInput2
          disablePortal={isDesktop ? false : true}
          label={dictionary.byRequestStatus}
          options={getAllRequestStatuses()}
          value={filtersData.status}
          onChange={(e) =>
            setFiltersData({ ...filtersData, status: e.target.value })
          }
          size={'small'}
        />
      </Box>
      <Stack component={'fieldset'} sx={{ width: isDesktop ? '47%' : '100%' }}>
        <FormLabel component={'legend'} sx={{ display: 'block' }}>
          <Typography variant={'body1'}>{dictionary.byRequestType}</Typography>
        </FormLabel>
        <Stack direction={'row'} gap={'10px'} width={'fit-content'}>
          <FormControlLabel
            control={
              <Checkbox
                icon={
                  <Avatar
                    alt={''}
                    src={UncheckedIcon}
                    sx={{ width: '24px', height: '24px' }}
                  />
                }
                checkedIcon={
                  <Avatar
                    alt={''}
                    src={CheckedIcon}
                    sx={{ width: '24px', height: '24px' }}
                  />
                }
                checked={filtersData.includePhoneRequests}
                onChange={() =>
                  setFiltersData({
                    ...filtersData,
                    includePhoneRequests: !filtersData.includePhoneRequests,
                    includeWebRequests: filtersData.includePhoneRequests
                      ? true
                      : filtersData.includeWebRequests,
                  })
                }
              />
            }
            label={
              <Typography variant={'body3'}>
                {dictionary.phoneApplication}
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                icon={
                  <Avatar
                    alt={''}
                    src={UncheckedIcon}
                    sx={{ width: '24px', height: '24px' }}
                  />
                }
                checkedIcon={
                  <Avatar
                    alt={''}
                    src={CheckedIcon}
                    sx={{ width: '24px', height: '24px' }}
                  />
                }
                checked={filtersData.includeWebRequests}
                onChange={() =>
                  setFiltersData({
                    ...filtersData,
                    includeWebRequests: !filtersData.includeWebRequests,
                    includePhoneRequests: filtersData.includeWebRequests
                      ? true
                      : filtersData.includePhoneRequests,
                  })
                }
              />
            }
            label={
              <Typography variant={'body3'}>
                {dictionary.onlineApplication}
              </Typography>
            }
          />
        </Stack>
      </Stack>
    </Stack>
  );
};
